import { useEffect, useMemo, useRef, useState } from 'react'
import { useMutation } from '@tanstack/react-query'
import { AlertTriangle, Check, ChevronRight, Download, FileSpreadsheet, Trash2, UploadCloud, XCircle } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { previewProductsImport, uploadImport } from '../../api/imports'
import { me as getMe } from '../../api/auth'
import { smartBack } from '../../utils/navigation'

const TEMPLATE_PATH = '/templates/products-import-template.xlsx'
const REQUIRED_COLUMNS = ['SKU', 'Name', 'UPC', 'Selling Price']
const SAMPLE_ROWS = [
  { sku: 'P001', name: 'Product 1', upc: '1234567890', price: '12.50' },
  { sku: 'P002', name: 'Product 2', upc: '0794522200450', price: '8.99' },
  { sku: 'P003', name: 'Product 3', upc: '0987654321', price: '22.00' },
]

const normalizeRoles = (u) => (u?.roles || (u?.role ? [u.role] : []))
  .map((r) => String(r || '').toLowerCase())

const badge = (status) => {
  if (status === 'valid') return <span className="import-hub-val-badge ok"><Check size={12} /> Valid</span>
  if (status === 'warn') return <span className="import-hub-val-badge warn"><AlertTriangle size={12} /> Warn</span>
  return <span className="import-hub-val-badge err"><XCircle size={12} /> Error</span>
}

export default function ImportMasters() {
  const nav = useNavigate()
  const [roles, setRoles] = useState(null)
  const [file, setFile] = useState(null)
  const [dragOver, setDragOver] = useState(false)
  const [disclosureOpen, setDisclosureOpen] = useState(true)
  const [result, setResult] = useState(null)
  const [uploadPct, setUploadPct] = useState(0)
  const fileInputRef = useRef(null)

  useEffect(() => {
    (async () => {
      try {
        const u = await getMe()
        setRoles(normalizeRoles(u))
      } catch {
        setRoles([])
      }
    })()
  }, [])

  const hasAccess = useMemo(() => {
    if (roles === null) return true
    if (roles.includes('admin')) return true
    if (roles.includes('purchase') || roles.includes('inventory')) return true
    return false
  }, [roles])

  const previewMut = useMutation({
    mutationFn: (f) => previewProductsImport(f),
  })

  const importMut = useMutation({
    mutationFn: (f) => uploadImport('products', f, {
      onUploadProgress: (evt) => {
        if (!evt?.total) return
        const pct = Math.min(100, Math.round((evt.loaded / evt.total) * 100))
        setUploadPct(pct)
      },
    }),
    onSuccess: (data) => {
      setUploadPct(100)
      setResult(data)
    },
    onError: (err) => {
      setResult({ error: err?.response?.data?.error || err.message || 'Import failed' })
    },
  })

  const pickFile = (nextFile) => {
    if (!nextFile) return
    if (!String(nextFile?.name || '').toLowerCase().endsWith('.xlsx')) {
      setResult({ error: 'Please upload a .xlsx Excel file.' })
      return
    }
    setFile(nextFile)
    setResult(null)
    setUploadPct(0)
    previewMut.reset()
    previewMut.mutate(nextFile)
  }

  const clearFile = () => {
    setFile(null)
    setDragOver(false)
    setResult(null)
    setUploadPct(0)
    previewMut.reset()
    importMut.reset()
    if (fileInputRef.current) fileInputRef.current.value = ''
  }

  const onImport = (e) => {
    e.preventDefault()
    if (!file) {
      setResult({ error: 'Choose an Excel (.xlsx) file first.' })
      return
    }
    setResult(null)
    setUploadPct(0)
    importMut.mutate(file)
  }

  const preview = previewMut.data || null
  const previewRows = Array.isArray(preview?.rows) ? preview.rows : []
  const previewMeta = {
    total: Number(preview?.total || 0),
    valid: Number(preview?.valid || 0),
    warn: Number(preview?.warn || 0),
    error: Number(preview?.error || 0),
  }

  const importedCount = Number(result?.inserted || 0) + Number(result?.updated || 0)
  const warningCount = Array.isArray(result?.warnings) ? result.warnings.length : 0
  const failedCount = Array.isArray(result?.errors) ? result.errors.length : (result?.error ? 1 : 0)

  const showPreviewCard = Boolean(file || previewMut.isPending || previewMut.isError || result)
  const showProgress = importMut.isLoading || uploadPct > 0
  const pct = importMut.isLoading ? Math.max(uploadPct, 12) : uploadPct

  if (!hasAccess) {
    return (
      <div className="import-hub">
        <h2 className="import-hub-title">Import Products</h2>
        <p className="muted">You do not have access to this page.</p>
      </div>
    )
  }

  return (
    <div className="import-hub">
      <div className="import-hub-header">
        <div className="import-hub-title-wrap">
          <div className="import-hub-breadcrumb">
            <button type="button" className="import-hub-link-btn" onClick={() => smartBack(nav, '/master/products')}>
              Products
            </button>
            <span>/</span>
            <span className="current">Import Products</span>
          </div>
          <h2 className="import-hub-title">Import Products</h2>
          <div className="import-hub-meta">Upload an Excel file to import product basics into Product Master</div>
        </div>
        <a href={TEMPLATE_PATH} download="products-import-template.xlsx" className="btn btn-ghost import-hub-template-btn">
          <Download size={14} />
          Download Excel Template
        </a>
      </div>

      <section className="import-hub-card import-hub-card-upload">
        <div className="import-hub-card-head">
          <div className="import-hub-card-title">
            <UploadCloud size={16} />
            Upload File
          </div>
        </div>
        <div className="import-hub-card-body">
          <label
            className={`import-hub-upload-zone${dragOver ? ' drag-over' : ''}`}
            onDragOver={(e) => { e.preventDefault(); setDragOver(true) }}
            onDragLeave={() => setDragOver(false)}
            onDrop={(e) => {
              e.preventDefault()
              setDragOver(false)
              const dropped = e.dataTransfer?.files?.[0]
              if (dropped) pickFile(dropped)
            }}
          >
            <input
              ref={fileInputRef}
              type="file"
              accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
              onChange={(e) => pickFile(e.target.files?.[0])}
            />
            <div className="import-hub-upload-icon">
              <FileSpreadsheet size={28} />
            </div>
            <div className="import-hub-upload-title">Drag and drop your Excel file here</div>
            <div className="import-hub-upload-sub">or click to browse (.xlsx supported)</div>
            {file && (
              <div className="import-hub-file-name">
                <Check size={14} />
                <span>{file.name}</span>
              </div>
            )}
          </label>

          <form className="import-hub-action-row" onSubmit={onImport}>
            <button type="submit" className="btn btn-brand import-hub-import-btn" disabled={!file || importMut.isLoading}>
              <UploadCloud size={14} />
              {importMut.isLoading ? 'Importing...' : 'Import'}
            </button>
            {file && (
              <button type="button" className="btn btn-ghost import-hub-clear-btn" onClick={clearFile}>
                <Trash2 size={14} />
                Clear
              </button>
            )}
            <span className="import-hub-file-hint">
              {!file ? 'Select a file to enable import' : 'File ready to import'}
            </span>
          </form>

          {showProgress && (
            <div className="import-hub-progress">
              <div className="import-hub-progress-label">{importMut.isLoading ? 'Uploading and importing...' : 'Import complete'}</div>
              <div className="import-hub-progress-track">
                <div className="import-hub-progress-fill" style={{ width: `${Math.min(100, Math.max(0, pct))}%` }} />
              </div>
            </div>
          )}

          {result?.error && (
            <div className="import-hub-inline-error">
              <XCircle size={14} />
              <span>{String(result.error)}</span>
            </div>
          )}
        </div>
      </section>

      <section className="import-hub-card">
        <div className="import-hub-card-head">
          <div className="import-hub-card-title">
            <AlertTriangle size={16} />
            File Specification
          </div>
        </div>
        <div className="import-hub-card-body import-hub-spec-body">
          <div className={`import-hub-disclosure${disclosureOpen ? ' open' : ''}`}>
            <button
              type="button"
              className="import-hub-disclosure-trigger"
              onClick={() => setDisclosureOpen((s) => !s)}
            >
              <ChevronRight className="import-hub-disclosure-arrow" size={14} />
              <span>Required columns</span>
            </button>
            {disclosureOpen && (
              <div className="import-hub-disclosure-body">
                <div className="import-hub-req-label">Products sheet — required</div>
                <div className="import-hub-pill-row">
                  {REQUIRED_COLUMNS.map((col) => (
                    <span key={col} className="import-hub-col-pill">{col}</span>
                  ))}
                </div>
                <p className="import-hub-req-note">
                  Only these fields are imported. All other product fields remain manual and can be edited later in Products & services.
                </p>
              </div>
            )}
          </div>

          <div className="import-hub-sample">
            <div className="import-hub-sample-label">Sample data format</div>
            <div className="import-hub-sample-wrap">
              <table className="import-hub-sample-table">
                <thead>
                  <tr>
                    <th>SKU</th>
                    <th>Name</th>
                    <th>UPC</th>
                    <th>Selling Price</th>
                  </tr>
                </thead>
                <tbody>
                  {SAMPLE_ROWS.map((row) => (
                    <tr key={row.sku}>
                      <td><span className="import-hub-sku-tag">{row.sku}</span></td>
                      <td>{row.name}</td>
                      <td className="muted">{row.upc}</td>
                      <td className="import-hub-price">{row.price}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </section>

      {showPreviewCard && (
        <section className="import-hub-card">
          <div className="import-hub-preview-head">
            <div>
              <div className="import-hub-preview-title">
                {previewMut.isPending ? 'Preview — validating rows...' : `Preview — ${previewMeta.total || 0} rows detected`}
              </div>
              <div className="import-hub-preview-meta">Showing first {Math.min(previewRows.length, 100)} rows</div>
            </div>
            <div className="import-hub-preview-badges">
              <span className="import-hub-preview-badge ok">{previewMeta.valid} valid</span>
              <span className="import-hub-preview-badge warn">{previewMeta.warn} warn</span>
              <span className="import-hub-preview-badge err">{previewMeta.error} error</span>
            </div>
          </div>

          {previewMut.isError && (
            <div className="import-hub-preview-error">
              <XCircle size={14} />
              <span>{previewMut.error?.response?.data?.error || previewMut.error?.message || 'Preview failed'}</span>
            </div>
          )}

          {!previewMut.isError && (
            <div className="import-hub-preview-table-wrap">
              <table className="import-hub-preview-table">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Status</th>
                    <th>SKU</th>
                    <th>Name</th>
                    <th>UPC</th>
                    <th>Selling Price</th>
                    <th>Issue</th>
                  </tr>
                </thead>
                <tbody>
                  {previewRows.length === 0 && !previewMut.isPending && (
                    <tr>
                      <td colSpan={7} className="import-hub-preview-empty">No preview rows yet.</td>
                    </tr>
                  )}
                  {previewRows.map((r) => (
                    <tr key={`${r.line}-${r.sku}-${r.name}`} className={`import-hub-row-${r.status || 'valid'}`}>
                      <td>{r.line}</td>
                      <td>{badge(r.status)}</td>
                      <td>{r.sku ? <span className="import-hub-sku-tag">{r.sku}</span> : <span className="muted">—</span>}</td>
                      <td>{r.name || <span className="muted">—</span>}</td>
                      <td className="muted">{r.upc || '—'}</td>
                      <td>{r.selling_price || '—'}</td>
                      <td className="muted">{r.issue || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {(result && !result.error) && (
            <div className="import-hub-result">
              <div className="import-hub-result-title">Import Complete</div>
              <div className="import-hub-result-grid">
                <div className="import-hub-result-cell green">
                  <div className="import-hub-result-val green">{importedCount}</div>
                  <div className="import-hub-result-lbl green">Imported</div>
                </div>
                <div className="import-hub-result-cell amber">
                  <div className="import-hub-result-val amber">{warningCount}</div>
                  <div className="import-hub-result-lbl amber">Warnings</div>
                </div>
                <div className="import-hub-result-cell red">
                  <div className="import-hub-result-val red">{failedCount}</div>
                  <div className="import-hub-result-lbl red">Failed</div>
                </div>
              </div>
            </div>
          )}
        </section>
      )}
    </div>
  )
}
